import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Plus,
  Edit,
  Trash2,
  Save,
  ArrowLeft,
  Clock,
  Users,
  CheckCircle,
  HelpCircle,
} from "lucide-react";
import quizService from "../../services/quizService";
import { Quiz, QuizQuestion } from "../../types/quiz";
import { toast } from "react-hot-toast";

const QuizEditor: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [quiz, setQuiz] = useState<Quiz | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showQuestionModal, setShowQuestionModal] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<QuizQuestion | null>(
    null
  );
  const [questionForm, setQuestionForm] = useState<QuizQuestion>(
    quizService.getDefaultQuestionData("multiple_choice")
  );

  useEffect(() => {
    if (id) {
      loadQuiz();
    } else {
      setLoading(false);
    }
  }, [id]);

  const loadQuiz = async () => {
    try {
      setLoading(true);
      const response = await quizService.getQuizById(id!);
      setQuiz(response.data);
    } catch (error) {
      console.error("Error loading quiz:", error);
      toast.error("Failed to load quiz");
      navigate("/elearning/quizzes");
    } finally {
      setLoading(false);
    }
  };

  const handleAddQuestion = () => {
    setQuestionForm(quizService.getDefaultQuestionData("multiple_choice"));
    setEditingQuestion(null);
    setShowQuestionModal(true);
  };

  const handleEditQuestion = (question: QuizQuestion) => {
    setQuestionForm({ ...question });
    setEditingQuestion(question);
    setShowQuestionModal(true);
  };

  const handleSaveQuestion = async () => {
    try {
      setSaving(true);

      if (editingQuestion) {
        await quizService.updateQuestion(
          quiz!._id!,
          editingQuestion._id!,
          questionForm
        );
        toast.success("Question updated successfully");
      } else {
        await quizService.addQuestion(quiz!._id!, questionForm);
        toast.success("Question added successfully");
      }

      setShowQuestionModal(false);
      loadQuiz(); // Reload to get updated quiz
    } catch (error: any) {
      console.error("Error saving question:", error);
      toast.error(error.response?.data?.message || "Failed to save question");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteQuestion = async (question: QuizQuestion) => {
    if (!confirm("Are you sure you want to delete this question?")) {
      return;
    }

    try {
      await quizService.removeQuestion(quiz!._id!, question._id!);
      toast.success("Question deleted successfully");
      loadQuiz(); // Reload to get updated quiz
    } catch (error: any) {
      console.error("Error deleting question:", error);
      toast.error(error.response?.data?.message || "Failed to delete question");
    }
  };

  const handleQuestionTypeChange = (type: QuizQuestion["type"]) => {
    setQuestionForm(quizService.getDefaultQuestionData(type));
  };

  const renderQuestionPreview = (question: QuizQuestion) => {
    switch (question.type) {
      case "multiple_choice":
        return (
          <div className="space-y-2">
            <p className="font-medium">{question.question}</p>
            <div className="space-y-1">
              {question.options?.map((option, index) => (
                <div key={index} className="flex items-center gap-2">
                  <input
                    type="radio"
                    name={`preview-${question._id}`}
                    disabled
                    checked={option === question.correctAnswer}
                    className="text-blue-600"
                  />
                  <span
                    className={
                      option === question.correctAnswer
                        ? "font-medium text-green-600"
                        : ""
                    }
                  >
                    {option}
                  </span>
                </div>
              ))}
            </div>
          </div>
        );

      case "true_false":
        return (
          <div className="space-y-2">
            <p className="font-medium">{question.question}</p>
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <input
                  type="radio"
                  disabled
                  checked={question.correctAnswer === "True"}
                  className="text-blue-600"
                />
                <span
                  className={
                    question.correctAnswer === "True"
                      ? "font-medium text-green-600"
                      : ""
                  }
                >
                  True
                </span>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="radio"
                  disabled
                  checked={question.correctAnswer === "False"}
                  className="text-blue-600"
                />
                <span
                  className={
                    question.correctAnswer === "False"
                      ? "font-medium text-green-600"
                      : ""
                  }
                >
                  False
                </span>
              </div>
            </div>
          </div>
        );

      case "short_answer":
      case "essay":
        return (
          <div className="space-y-2">
            <p className="font-medium">{question.question}</p>
            <div className="p-3 bg-gray-100 rounded border-dashed border-2">
              <p className="text-gray-500 text-sm">
                {question.type === "essay"
                  ? "Essay answer area"
                  : "Short answer area"}
              </p>
            </div>
            {question.correctAnswer && (
              <p className="text-sm text-green-600">
                <strong>Sample Answer:</strong> {question.correctAnswer}
              </p>
            )}
          </div>
        );

      default:
        return (
          <div>
            <p className="font-medium">{question.question}</p>
          </div>
        );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!quiz) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-600">Quiz not found</p>
        <button
          onClick={() => navigate("/elearning/quizzes")}
          className="mt-4 text-blue-600 hover:underline"
        >
          Back to Quizzes
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate("/elearning/quizzes")}
            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{quiz.title}</h1>
            <p className="text-gray-600">{quiz.description}</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div className="text-sm text-gray-500 space-y-1">
            <div className="flex items-center gap-1">
              <HelpCircle size={16} />
              <span>{quiz.questions.length} questions</span>
            </div>
            <div className="flex items-center gap-1">
              <Clock size={16} />
              <span>
                {quiz.timeLimit > 0 ? `${quiz.timeLimit} min` : "No limit"}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Quiz Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg border">
          <div className="flex items-center gap-2">
            <HelpCircle className="text-blue-600" size={20} />
            <div>
              <p className="text-sm text-gray-600">Questions</p>
              <p className="text-xl font-bold">{quiz.questions.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg border">
          <div className="flex items-center gap-2">
            <Clock className="text-green-600" size={20} />
            <div>
              <p className="text-sm text-gray-600">Time Limit</p>
              <p className="text-xl font-bold">
                {quiz.timeLimit > 0 ? `${quiz.timeLimit}m` : "None"}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg border">
          <div className="flex items-center gap-2">
            <CheckCircle className="text-purple-600" size={20} />
            <div>
              <p className="text-sm text-gray-600">Passing Score</p>
              <p className="text-xl font-bold">{quiz.passingScore}%</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg border">
          <div className="flex items-center gap-2">
            <Users className="text-orange-600" size={20} />
            <div>
              <p className="text-sm text-gray-600">Total Points</p>
              <p className="text-xl font-bold">{quiz.totalPoints || 0}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Questions List */}
      <div className="bg-white rounded-lg border">
        <div className="p-4 border-b flex items-center justify-between">
          <h2 className="text-lg font-semibold">Questions</h2>
          <button
            onClick={handleAddQuestion}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <Plus size={18} />
            Add Question
          </button>
        </div>

        {quiz.questions.length === 0 ? (
          <div className="p-8 text-center">
            <HelpCircle className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              No questions yet
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              Add your first question to get started.
            </p>
            <button
              onClick={handleAddQuestion}
              className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
            >
              Add Question
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {quiz.questions.map((question, index) => (
              <div key={question._id} className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-sm font-medium">
                        Q{index + 1}
                      </span>
                      <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs">
                        {question.type.replace("_", " ")}
                      </span>
                      <span className="text-sm text-gray-500">
                        {question.points}{" "}
                        {question.points === 1 ? "point" : "points"}
                      </span>
                    </div>

                    <div className="mb-3">
                      {renderQuestionPreview(question)}
                    </div>

                    {question.explanation && (
                      <div className="mt-2 p-2 bg-yellow-50 border-l-4 border-yellow-400">
                        <p className="text-sm text-yellow-800">
                          <strong>Explanation:</strong> {question.explanation}
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-1 ml-4">
                    <button
                      onClick={() => handleEditQuestion(question)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                      title="Edit Question"
                    >
                      <Edit size={16} />
                    </button>
                    <button
                      onClick={() => handleDeleteQuestion(question)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded"
                      title="Delete Question"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Question Modal */}
      {showQuestionModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">
              {editingQuestion ? "Edit Question" : "Add New Question"}
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Question Type *
                </label>
                <select
                  value={questionForm.type}
                  onChange={(e) =>
                    handleQuestionTypeChange(
                      e.target.value as QuizQuestion["type"]
                    )
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="multiple_choice">Multiple Choice</option>
                  <option value="true_false">True/False</option>
                  <option value="short_answer">Short Answer</option>
                  <option value="essay">Essay</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Question Text *
                </label>
                <textarea
                  value={questionForm.question}
                  onChange={(e) =>
                    setQuestionForm({
                      ...questionForm,
                      question: e.target.value,
                    })
                  }
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter your question..."
                />
              </div>

              {(questionForm.type === "multiple_choice" ||
                questionForm.type === "true_false") && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Answer Options *
                  </label>
                  <div className="space-y-2">
                    {questionForm.options?.map((option, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <input
                          type="radio"
                          name="correctAnswer"
                          checked={option === questionForm.correctAnswer}
                          onChange={() =>
                            setQuestionForm({
                              ...questionForm,
                              correctAnswer: option,
                            })
                          }
                          className="text-blue-600"
                        />
                        <input
                          type="text"
                          value={option}
                          onChange={(e) => {
                            const newOptions = [
                              ...(questionForm.options || []),
                            ];
                            newOptions[index] = e.target.value;
                            setQuestionForm({
                              ...questionForm,
                              options: newOptions,
                            });
                          }}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder={`Option ${index + 1}`}
                          disabled={questionForm.type === "true_false"}
                        />
                      </div>
                    ))}
                  </div>
                  {questionForm.type === "multiple_choice" && (
                    <p className="text-sm text-gray-500 mt-1">
                      Select the correct answer by clicking the radio button
                    </p>
                  )}
                </div>
              )}

              {(questionForm.type === "short_answer" ||
                questionForm.type === "essay") && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Sample/Expected Answer
                  </label>
                  <textarea
                    value={questionForm.correctAnswer || ""}
                    onChange={(e) =>
                      setQuestionForm({
                        ...questionForm,
                        correctAnswer: e.target.value,
                      })
                    }
                    rows={questionForm.type === "essay" ? 4 : 2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter sample answer for reference..."
                  />
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Points *
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={questionForm.points || ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      const numValue = value === "" ? "" : parseInt(value) || 1;
                      setQuestionForm({
                        ...questionForm,
                        points: numValue,
                      });
                    }}
                    placeholder="1"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Difficulty
                  </label>
                  <select
                    value={questionForm.difficulty}
                    onChange={(e) =>
                      setQuestionForm({
                        ...questionForm,
                        difficulty: e.target.value as
                          | "easy"
                          | "medium"
                          | "hard",
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="easy">Easy</option>
                    <option value="medium">Medium</option>
                    <option value="hard">Hard</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Explanation (Optional)
                </label>
                <textarea
                  value={questionForm.explanation || ""}
                  onChange={(e) =>
                    setQuestionForm({
                      ...questionForm,
                      explanation: e.target.value,
                    })
                  }
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Explain why this is the correct answer..."
                />
              </div>

              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => setShowQuestionModal(false)}
                  className="flex-1 px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveQuestion}
                  disabled={saving || !questionForm.question.trim()}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {saving ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  ) : (
                    <Save size={16} />
                  )}
                  {saving
                    ? "Saving..."
                    : editingQuestion
                    ? "Update Question"
                    : "Add Question"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuizEditor;
